import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { useCallback, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { CorrectManagementForm, GetManagementForm } from '../../../../services/peerToPeer/ManagementService'
import { handleApiResponse } from '../../../../utils/apiHelpers'
import { SurveyType } from '../../../../models/SurveySchema'
import { STARTSurveyDisplay, START_SURVEY_THEME, type STARTSurveyDisplayHandle } from '../../../customComponents/survey/STARTSurveyDisplay'

interface Props {
  formId: number
  open: boolean
  onClose: () => void
  onSaved: () => void
}

/**
 * The correction dialog reuses the schema-driven collection form, but its autosave hook only
 * records the latest answers locally: a correction is committed once, on "Save correction",
 * so Cancel leaves the submission untouched and the backend can reject a correction that
 * empties a required field before anything is persisted.
 */
export const CorrectionDialog = ({ formId, open, onClose, onSaved }: Props): JSX.Element => {
  const [isSaving, setIsSaving] = useState(false)
  const surveyRef = useRef<STARTSurveyDisplayHandle>(null)
  const latestAnswersRef = useRef<string | null>(null)

  const loadOverride = useCallback(async (): Promise<{ id: SurveyType, content: string, answers?: string } | null> => {
    const form = await handleApiResponse(GetManagementForm(formId))
    if (form == null) return null
    latestAnswersRef.current = form.answers
    return { id: SurveyType.PeerToPeer, content: form.schema, answers: form.answers ?? undefined }
  }, [formId])

  const saveOverride = useCallback(async (answersJson: string): Promise<void> => {
    latestAnswersRef.current = answersJson
  }, [])

  const save = async (): Promise<void> => {
    setIsSaving(true)
    await surveyRef.current?.flushPendingSave()
    const result = await CorrectManagementForm(formId, latestAnswersRef.current ?? '{}')
    setIsSaving(false)

    if (result.success) {
      toast.success('Correction saved')
      onSaved()
      return
    }
    const errors = result.errors.length > 0 ? result.errors : ['The correction could not be saved.']
    errors.forEach(error => { toast.error(error) })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>Correct building form</DialogTitle>
      <DialogContent data-testid='p2pCorrectionForm'>
        <STARTSurveyDisplay
          ref={surveyRef}
          annualPlanId={0}
          planOrgId={0}
          surveyType={SurveyType.PeerToPeer}
          loadOverride={loadOverride}
          saveOverride={saveOverride}
          surveyThemeVariables={START_SURVEY_THEME}
          hideNavigation
        />
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose} data-testid='p2pCorrectionCancel' sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant='contained' disabled={isSaving} onClick={() => { void save() }} data-testid='p2pCorrectionSave' sx={{ textTransform: 'none' }}>Save correction</Button>
      </DialogActions>
    </Dialog>
  )
}
